import React from 'react';
import { motion } from 'framer-motion';
import { Cpu, Droplets, Wifi, BarChart3 } from 'lucide-react';

const content = {
  en: {
    eyebrow: '4D INNOVATION',
    headline: 'The Technology of Tomorrow, Deployed Today',
    subheadline: 'Our systems don\'t just solve problems—they anticipate them.',
    technologies: [
      {
        icon: Cpu,
        code: 'SYS.001',
        title: 'AI-Powered Purification',
        description: 'Machine learning algorithms that adapt filtration parameters in real-time based on water quality sensors.',
        metric: '99.9% purity',
      },
      {
        icon: Droplets,
        code: 'SYS.002',
        title: 'Atmospheric Water Generation',
        description: 'Extract water from air using solar-powered condensation systems. Works in deserts and humid regions alike.',
        metric: '1000L/day',
      },
      {
        icon: Wifi,
        code: 'SYS.003',
        title: 'IoT Monitoring Network',
        description: 'Every installation connects to our global network. Remote diagnostics, predictive maintenance, instant alerts.',
        metric: '24/7 uptime',
      },
      {
        icon: BarChart3,
        code: 'SYS.004',
        title: 'Blockchain Transparency',
        description: 'Every donation, every installation, every litre delivered—permanently recorded and publicly verifiable.',
        metric: '100% traceable',
      },
    ],
    bottomQuote: 'The 4th dimension is time. We build for generations, not quarters.',
  },
  es: {
    eyebrow: 'INNOVACIÓN 4D',
    headline: 'La Tecnología del Mañana, Desplegada Hoy',
    subheadline: 'Nuestros sistemas no solo resuelven problemas—los anticipan.',
    technologies: [
      {
        icon: Cpu,
        code: 'SYS.001',
        title: 'Purificación con IA',
        description: 'Algoritmos de aprendizaje automático que adaptan los parámetros de filtración en tiempo real basados en sensores de calidad del agua.',
        metric: '99.9% pureza',
      },
      {
        icon: Droplets,
        code: 'SYS.002',
        title: 'Generación de Agua Atmosférica',
        description: 'Extraer agua del aire usando sistemas de condensación alimentados por energía solar. Funciona en desiertos y regiones húmedas.',
        metric: '1000L/día',
      },
      {
        icon: Wifi,
        code: 'SYS.003',
        title: 'Red de Monitoreo IoT',
        description: 'Cada instalación se conecta a nuestra red global. Diagnósticos remotos, mantenimiento predictivo, alertas instantáneas.',
        metric: '24/7 activo',
      },
      {
        icon: BarChart3,
        code: 'SYS.004',
        title: 'Transparencia Blockchain',
        description: 'Cada donación, cada instalación, cada litro entregado—registrado permanentemente y verificable públicamente.',
        metric: '100% rastreable',
      },
    ],
    bottomQuote: 'La 4ta dimensión es el tiempo. Construimos para generaciones, no trimestres.',
  },
};

export const TechnologySection = ({ language }) => {
  const t = content[language];

  return (
    <section 
      id="technology" 
      className="py-24 md:py-32 relative overflow-hidden"
      data-testid="technology-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0F2645] via-[#0A1F3A] to-[#051120]" />
      
      {/* Grid Pattern Overlay */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `linear-gradient(rgba(201, 169, 107, 0.3) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(201, 169, 107, 0.3) 1px, transparent 1px)`,
          backgroundSize: '50px 50px',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 md:mb-24"
        >
          <p className="eyebrow-text mb-4">{t.eyebrow}</p>
          <h2 className="section-heading font-heading text-[#E6F1FF]">
            {t.headline}
          </h2>
          <p className="section-subheading mx-auto mt-4">
            {t.subheadline}
          </p>
        </motion.div>

        {/* Technologies Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {t.technologies.map((tech, index) => {
            const Icon = tech.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="glass rounded-2xl p-6 md:p-8 group hover:border-[#C9A96B]/50 transition-all duration-500"
                data-testid={`tech-card-${index}`}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className="w-12 h-12 rounded-xl bg-[#C9A96B]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A96B]/20 transition-colors">
                    <Icon className="w-6 h-6 text-[#C9A96B]" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-xs text-[#C9A96B]/60">{tech.code}</span>
                    </div>
                    <h3 className="font-heading text-xl text-[#E6F1FF] mb-2">
                      {tech.title}
                    </h3>
                    <p className="text-sm text-[#8892B0] leading-relaxed mb-4">
                      {tech.description}
                    </p>
                    
                    {/* Metric Badge */}
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C9A96B]/10 border border-[#C9A96B]/30">
                      <div className="w-2 h-2 rounded-full bg-[#64FFDA] animate-pulse" />
                      <span className="font-mono text-xs text-[#C9A96B] uppercase tracking-wider">
                        {tech.metric}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom Quote */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center mt-16 md:mt-24"
        >
          <p className="quote-text max-w-2xl mx-auto text-[#C9A96B]">
            "{t.bottomQuote}"
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default TechnologySection;
